import { useContext, useEffect, useMemo, useState } from "react";
import { AlertComponent, NavBar } from "../../components";
import DataContext from "../../context/DataContext";
import { useFetch, usePermissions } from "../../hooks";
import { usePaginator } from "../../hooks/usePaginator";
import { SalesItemProp } from "../../interfaces/interfaces";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { Box, Button, Container, Fade, IconButton, Paper, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import { useNavigate } from "react-router-dom";
import { useDate } from "../../hooks/useDate";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import {
  ListColumn,
  ListPageLayout,
} from "../../components/shared/ListPageLayout";

interface FetchResponse {
  success?: boolean;
  data: SalesItemProp[];
}

export const SalesPage = () => {
  const { paginationModel, setPaginationModel } = usePaginator("sales");
  const [search, setSearch] = useState("");
  const fetchFilters = useMemo(() => ({ search }), [search]);
  const { data, error, isLoading, pagination } = useFetch<FetchResponse>(
    "sales-paginate",
    paginationModel,
    undefined,
    undefined,
    fetchFilters,
  );
  const { isSuccess, isError, onResetValues, fetchDataSales } =
    useContext(DataContext);
  const navigate = useNavigate();
  const { getDateFromString } = useDate();
  const { canManageSales } = usePermissions();
  const [tableData, setTableData] = useState<SalesItemProp[]>([]);

  useEffect(() => {
    data && data.data && setTableData(data.data);
  }, [data]);

  const columns: ListColumn<SalesItemProp>[] = [
    {
      key: "orderDate",
      header: "Order Date",
      render: (r) => (r.orderDate ? getDateFromString(r.orderDate) : ""),
      sortAccessor: (r) => (r.orderDate ? new Date(r.orderDate).getTime() : null),
      width: "1fr",
    },
    {
      key: "client",
      header: "Client",
      render: (r) => r.client,
      sortAccessor: (r) => r.client ?? "",
      width: "1.4fr",
    },
    {
      key: "wellName",
      header: "Well",
      render: (r) => r.wellName,
      sortAccessor: (r) => r.wellName ?? "",
      width: "1.4fr",
    },
    {
      key: "deliveryContact",
      header: "Delivery Contact",
      render: (r) => r.deliveryContact,
      sortAccessor: (r) => r.deliveryContact ?? "",
      width: "1.4fr",
    },
    {
      key: "salesmanContact",
      header: "Salesman Contact",
      render: (r) => r.salesmanContact,
      sortAccessor: (r) => r.salesmanContact ?? "",
      width: "1.4fr",
    },
  ];

  return (
    <>
      <NavBar title="Sales Orders" buttons={[]} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <ListPageLayout<SalesItemProp>
                title="Sales Orders"
                columns={columns}
                rows={tableData}
                getRowId={(r) => r.id}
                rowsLoading={isLoading}
                isError={Boolean(error)}
                errorMessage="Sales orders could not be loaded."
                searchValue={search}
                onSearchChange={(value) => setSearch(value.trim())}
                searchPlaceholder="Search by quote number, client, or well…"
                page={paginationModel.page}
                pageSize={paginationModel.pageSize}
                totalCount={Number(pagination?.totalRecords || 0)}
                onPageChange={(page) =>
                  setPaginationModel({ ...paginationModel, page })
                }
                onPageSizeChange={(pageSize) =>
                  setPaginationModel({ page: 0, pageSize })
                }
                toolbarActions={
                  canManageSales ? (
                    <Button
                      startIcon={<AddBoxIcon />}
                      onClick={() => {
                        onResetValues();
                        navigate("/sales/new/0");
                      }}
                    >
                      New
                    </Button>
                  ) : undefined
                }
                rowActions={(r) => (
                  <>
                    {canManageSales && (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => navigate(`/sales/edit/${r.id}`)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManageSales && (
                      <Tooltip title="Duplicate">
                        <IconButton
                          onClick={() => {
                            fetchDataSales(r.id);
                            navigate("/sales/new/0");
                          }}
                          size="small"
                        >
                          <FileCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Print">
                      <IconButton
                        onClick={() => navigate(`/sales/${r.id}`)}
                        size="small"
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              />
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
    </>
  );
};
